import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Chip, withStyles } from "@material-ui/core";
import PhoneIcon from "@material-ui/icons/Phone";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import leadsApi from "../../apis/leadsApi";
import LeadHistoryModal from "../Modal/LeadHistory";

const PanelBox = withStyles(() => ({
  root: {
    border: "1px solid #E5E7EB",
    borderRadius: 16,
    padding: "18px 20px",
    marginBottom: 24,
    backgroundColor: "#ffffff",
  },
}))(Box);

const RowBox = withStyles(() => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid #F3F4F6",
    "&:last-child": {
      borderBottom: "none",
    },
  },
}))(Box);

const extractLeads = (response) => {
  const data = response?.data;
  if (!Array.isArray(data)) return [];
  return data.map((item) => ({ id: item.id, ...item.attributes }));
};

// The morning list: leads whose follow-up is due today or slipped past.
// Nurturing leads IS the revenue engine — this puts the day's calls on the
// Home page so nobody has to remember to run a report (the FirstClass way).
const FollowUpsToday = () => {
  const [due, setDue] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeLead, setActiveLead] = useState(null);

  const fetchLists = useCallback(async () => {
    try {
      const [dueRes, overdueRes] = await Promise.all([
        leadsApi.getList({ follow_up: "due_today" }),
        leadsApi.getList({ follow_up: "overdue" }),
      ]);
      setDue(extractLeads(dueRes));
      setOverdue(extractLeads(overdueRes));
    } catch (e) {
      // Roles without lead access just don't get the panel.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const rows = [...overdue, ...due];
  if (!loaded || rows.length === 0) return null;

  return (
    <>
      <PanelBox className="juice-in">
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 6,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <Typography style={{ fontSize: 17, fontWeight: 600 }}>
            📞 Today&apos;s follow-ups
          </Typography>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon style={{ fontSize: 15 }} />}
            onClick={() => (window.location.href = "/leads")}
            style={{ textTransform: "none", color: "#4C51BF", fontWeight: 600 }}
          >
            All leads
          </Button>
        </Box>
        <Typography style={{ fontSize: 13, color: "#6B7280", marginBottom: 8 }}>
          {overdue.length > 0
            ? `${rows.length} ${rows.length === 1 ? "person" : "people"} waiting on you, ${overdue.length} overdue.`
            : `${rows.length} ${rows.length === 1 ? "person" : "people"} to reach out to today.`}
        </Typography>

        {rows.slice(0, 6).map((lead) => {
          const isOverdue = overdue.some((l) => l.id === lead.id);
          return (
            <RowBox key={lead.id}>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Box style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <Typography style={{ fontSize: 14, fontWeight: 600 }}>
                    {lead.first_name} {lead.last_name}
                  </Typography>
                  {isOverdue && (
                    <Chip
                      label="Overdue"
                      size="small"
                      style={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        backgroundColor: "#FEE2E2",
                        color: "#B91C1C",
                      }}
                    />
                  )}
                </Box>
                <Typography
                  style={{
                    fontSize: 12,
                    color: "#6B7280",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lead.next_follow_up_note || lead.phone || lead.parent_phone || lead.contact_email || ""}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhoneIcon style={{ fontSize: 14 }} />}
                onClick={() => setActiveLead(lead)}
                style={{ borderRadius: 18, textTransform: "none", flexShrink: 0 }}
              >
                Log touch
              </Button>
            </RowBox>
          );
        })}

        {rows.length > 6 && (
          <Typography style={{ fontSize: 12, color: "#6B7280", marginTop: 8 }}>
            +{rows.length - 6} more on the leads page
          </Typography>
        )}
      </PanelBox>

      {activeLead && (
        <LeadHistoryModal
          isOpen={Boolean(activeLead)}
          lead={activeLead}
          onClose={() => setActiveLead(null)}
          onLeadUpdated={() => {
            // A logged touch usually reschedules the follow-up — refresh so
            // the handled lead leaves today's list.
            fetchLists();
          }}
        />
      )}
    </>
  );
};

export default FollowUpsToday;
